import {Injectable, OnDestroy, Renderer2, RendererFactory2} from '@angular/core';
import {BreakpointObserver} from '@angular/cdk/layout';
import {ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router} from '@angular/router';
import {BehaviorSubject, Observable, Subject, combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, startWith, takeUntil} from 'rxjs/operators';
import {LoaderService} from '@app/services/loader.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {
  NavLayoutState, ROOT_CLASSES, Viewport, computeLayout, rootClassesFor,
} from '@app/_components/navigation/model/nav-layout';

export interface NavRouteData {
  /** The menu entry a page belongs to when its URL alone does not say. */
  navParent: string | null;
  /** The page's own name for the last breadcrumb. */
  navTitle: string | null;
}

const sameState = (a: NavLayoutState, b: NavLayoutState) => a.visible === b.visible && a.topLayout === b.topLayout
  && a.presentation === b.presentation && a.drawerOpen === b.drawerOpen && a.canCollapse === b.canCollapse
  && a.viewport === b.viewport;

/**
 * Where the navigation sits: the top menu, the docked side menu (expanded or a rail of icons),
 * or a drawer - decided by nav-layout.ts#computeLayout from the person's choice, the window
 * and the page. The result is put on <html> as classes that set --sn-sidebar-width, which the
 * page and everything fixed to the left edge offset by.
 */
@Injectable({providedIn: 'root'})
export class NavLayoutService implements OnDestroy {
  private readonly renderer: Renderer2;
  private readonly destroyed = new Subject<void>();
  private readonly authenticatedSubject = new BehaviorSubject<boolean>(false);
  private readonly drawerSubject = new BehaviorSubject<boolean>(false);
  private readonly narrowExpandedSubject = new BehaviorSubject<boolean>(false);
  private readonly paletteSubject = new BehaviorSubject<boolean>(false);
  private readonly focusSearchSubject = new Subject<void>();
  private readonly routeDataSubject = new BehaviorSubject<NavRouteData>({navParent: null, navTitle: null});
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private stateSnapshot: NavLayoutState | null = null;

  readonly routeData$: Observable<NavRouteData> = this.routeDataSubject.asObservable();
  readonly paletteOpen$: Observable<boolean> = this.paletteSubject.pipe(distinctUntilChanged());
  /** Put the cursor in the side menu's own search box. */
  readonly focusSearch$: Observable<void> = this.focusSearchSubject.asObservable();

  readonly viewport$: Observable<Viewport> = this.breakpoints
    .observe(['(max-width: 767.98px)', '(max-width: 1023.98px)'])
    .pipe(
      map((result) => result.breakpoints['(max-width: 767.98px)'] ? 'mobile'
        : result.breakpoints['(max-width: 1023.98px)'] ? 'narrow' : 'wide'),
      distinctUntilChanged()
    ) as Observable<Viewport>;

  readonly state$: Observable<NavLayoutState> = combineLatest([
    this.authenticatedSubject,
    this.loaderService.showNavbar$,
    this.navigationState.menuMode$,
    this.navigationState.preferences$.pipe(map((preferences) => preferences.sidebarCollapsed), distinctUntilChanged()),
    this.viewport$,
    this.drawerSubject,
    this.narrowExpandedSubject,
  ]).pipe(
    map(([authenticated, showNavbar, mode, collapsed, viewport, drawerOpen, narrowExpanded]) =>
      computeLayout({authenticated, showNavbar: showNavbar !== false, mode, collapsed, viewport, drawerOpen, narrowExpanded})),
    distinctUntilChanged(sameState),
    shareReplay({bufferSize: 1, refCount: false})
  );

  constructor(
    rendererFactory: RendererFactory2,
    private breakpoints: BreakpointObserver,
    private router: Router,
    private loaderService: LoaderService,
    private navigationState: NavigationStateService,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.state$.pipe(takeUntil(this.destroyed)).subscribe((state) => this.apply(state));
    this.router.events.pipe(filter((event) => event instanceof NavigationStart), takeUntil(this.destroyed))
      .subscribe(() => {
        this.drawerSubject.next(false);
        this.narrowExpandedSubject.next(false);
        this.paletteSubject.next(false);
      });
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd), startWith(null), takeUntil(this.destroyed))
      .subscribe(() => this.routeDataSubject.next(this.readRouteData(this.router.routerState.snapshot.root)));
    if (typeof window !== 'undefined') {
      // Two frames, so the first paint lands in place instead of sliding in.
      requestAnimationFrame(() => requestAnimationFrame(() =>
        this.renderer.addClass(document.documentElement, 'sn-nav-ready')));
    }
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }

  get state(): NavLayoutState | null {
    return this.stateSnapshot;
  }

  setAuthenticated(authenticated: boolean): void {
    if (this.authenticatedSubject.value !== authenticated) this.authenticatedSubject.next(authenticated);
  }

  openDrawer(): void {
    this.drawerSubject.next(true);
  }

  closeDrawer(): void {
    this.drawerSubject.next(false);
  }

  toggleDrawer(): void {
    this.drawerSubject.next(!this.drawerSubject.value);
  }

  /**
   * The collapse toggle: on a wide window it docks the menu open or as a rail (and is
   * remembered); on a narrow one it floats the full menu over the page for a moment, leaving
   * the saved choice alone.
   */
  toggleCollapsed(): void {
    if (this.stateSnapshot?.viewport === 'narrow') {
      this.narrowExpandedSubject.next(!this.narrowExpandedSubject.value);
      return;
    }
    this.navigationState.setSidebarCollapsed(!this.navigationState.preferences.sidebarCollapsed);
  }

  closeOverlay(): void {
    this.narrowExpandedSubject.next(false);
  }

  openPalette(): void {
    this.paletteSubject.next(true);
  }

  closePalette(): void {
    this.paletteSubject.next(false);
  }

  /** Ctrl/Cmd+K and the search buttons: the side menu's own box when it is open, else the palette. */
  requestSearch(): void {
    if (this.stateSnapshot?.presentation === 'expanded' || this.stateSnapshot?.presentation === 'overlay'
      || (this.stateSnapshot?.presentation === 'drawer' && this.stateSnapshot.drawerOpen)) {
      this.focusSearchSubject.next();
    } else {
      this.openPalette();
    }
  }

  private apply(state: NavLayoutState): void {
    const previous = this.stateSnapshot;
    this.stateSnapshot = state;
    const root = document.documentElement;
    const wanted = rootClassesFor(state);
    ROOT_CLASSES.forEach((name) => {
      if (wanted.includes(name)) this.renderer.addClass(root, name); else this.renderer.removeClass(root, name);
    });
    if (previous && previous.presentation !== state.presentation && typeof window !== 'undefined') {
      // Tables and charts measure their container once; tell them it moved.
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => window.dispatchEvent(new Event('resize')), 220);
    }
  }

  private readRouteData(root: ActivatedRouteSnapshot): NavRouteData {
    const data: NavRouteData = {navParent: null, navTitle: null};
    let route: ActivatedRouteSnapshot | null = root;
    while (route) {
      if (route.data?.navParent) data.navParent = route.data.navParent;
      if (route.data?.navTitle) data.navTitle = route.data.navTitle;
      route = route.firstChild;
    }
    return data;
  }
}
